import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Map as MapIcon, List, X, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MapView } from '@/components/map/MapView';
import { ProviderCard } from '@/components/listings/ProviderCard';
import { FilterPanel } from '@/components/search/FilterPanel';
import type { ServiceProvider, SearchFilters, MapBounds, ServiceType } from '@/types';
import { useProviders } from '@/hooks/useProviders';

export function Search() {
  const { t } = useTranslation('search');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [hoveredProviderId, setHoveredProviderId] = useState<string | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBounds | undefined>();

  // Build filters from URL params (location comes from header search)
  const filters: SearchFilters = {
    location: searchParams.get('loc') || undefined,
    serviceTypes: searchParams.get('type')
      ? [searchParams.get('type') as ServiceType]
      : undefined,
    minRating: searchParams.get('rating')
      ? Number(searchParams.get('rating'))
      : undefined,
    maxDistance: searchParams.get('distance')
      ? Number(searchParams.get('distance'))
      : undefined,
    isVerified: searchParams.get('verified') === 'true' || undefined,
  };

  // Fetch providers
  const { data: providers = [], isLoading } = useProviders(filters, mapBounds);

  // Handle filter changes (update URL)
  const handleFiltersChange = useCallback((newFilters: SearchFilters) => {
    const params = new URLSearchParams(searchParams);

    // Update filter params
    if (newFilters.serviceTypes?.length) {
      params.set('type', newFilters.serviceTypes[0]);
    } else {
      params.delete('type');
    }
    if (newFilters.minRating) {
      params.set('rating', String(newFilters.minRating));
    } else {
      params.delete('rating');
    }
    if (newFilters.maxDistance) {
      params.set('distance', String(newFilters.maxDistance));
    } else {
      params.delete('distance');
    }
    if (newFilters.isVerified) {
      params.set('verified', 'true');
    } else {
      params.delete('verified');
    }

    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    setMapBounds(bounds);
  }, []);

  const handleProviderSelect = useCallback((provider: ServiceProvider | null) => {
    setSelectedProviderId(provider?.id || null);
  }, []);

  const activeFilterCount = [
    filters.serviceTypes?.length,
    filters.minRating,
    filters.maxDistance,
    filters.isVerified,
  ].filter(Boolean).length;

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      {/* Compact toolbar for mobile controls */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {isLoading ? t('loading') : t('resultsCount', { count: providers.length })}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {t('filters')}
            {activeFilterCount > 0 && (
              <span className="ml-1 w-5 h-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowMap(!showMap)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
          >
            {showMap ? (
              <>
                <List className="w-4 h-4" />
                {t('list')}
              </>
            ) : (
              <>
                <MapIcon className="w-4 h-4" />
                {t('map')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Filters Sidebar */}
        <aside className="hidden lg:block w-72 flex-shrink-0 border-r border-gray-200 overflow-y-auto p-4">
          <FilterPanel filters={filters} onChange={handleFiltersChange} />
        </aside>

        {/* Results List */}
        <div
          className={`
            flex-1 overflow-y-auto p-4
            ${showMap ? 'hidden lg:block lg:w-1/2' : 'block'}
          `}
        >
          <div className="mb-4 hidden lg:block">
            <p className="text-sm text-gray-600">
              {isLoading
                ? t('loading')
                : t('resultsCount', { count: providers.length })}
            </p>
          </div>
          <div className="space-y-4">
            {providers.map((provider) => (
              <ProviderCard
                key={provider.id}
                provider={provider}
                onHover={setHoveredProviderId}
                isHighlighted={selectedProviderId === provider.id}
              />
            ))}
            {!isLoading && providers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">{t('noResults')}</p>
                <button
                  onClick={() => handleFiltersChange({})}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  {t('clearFilters')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        <div
          className={`
            flex-1 p-4
            ${showMap ? 'block' : 'hidden lg:block'}
            lg:w-1/2
          `}
        >
          <div className="h-full rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <MapView
              providers={providers}
              onBoundsChange={handleBoundsChange}
              selectedProviderId={selectedProviderId}
              onProviderSelect={handleProviderSelect}
              hoveredProviderId={hoveredProviderId}
              fitBounds
            />
          </div>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowFilters(false)}
          />
          <div className="absolute inset-y-0 left-0 w-full max-w-sm bg-white overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">{t('filters')}</h2>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <FilterPanel
                filters={filters}
                onChange={handleFiltersChange}
                onClose={() => setShowFilters(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
